import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  confirmPasswordReset,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  User as FirebaseUser,
  verifyPasswordResetCode as fbVerifyPasswordResetCode,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from './config';
import { User } from '../types/user';

// Convertir un FirebaseUser en User avec les champs supplémentaires
export const transformFirebaseUser = async (firebaseUser: FirebaseUser): Promise<User> => {
  // Récupérer les claims personnalisés du token
  const token = await firebaseUser.getIdTokenResult();
  const role = (token.claims.role as string) || 'CLIENT';
  const networks = (token.claims.networks as string[]) || [];
  
  return {
    id: firebaseUser.uid,
    email: firebaseUser.email,
    name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
    role,
    networks
  };
};

// Connexion avec email et mot de passe
export const loginWithEmailAndPassword = async (
  email: string, 
  password: string, 
  rememberMe: boolean = false
): Promise<User> => {
  // Définir la persistance selon l'option "Se souvenir de moi"
  await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
  
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return transformFirebaseUser(userCredential.user);
};

// Nouvelle fonction pour se connecter avec Google
export const loginWithGoogle = async (): Promise<User | null> => {
  try {
    const provider = new GoogleAuthProvider();
    // Ne pas demander les scopes pour Gmail ou Calendar ici
    // On veut juste l'authentification de base
    
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    
    // Transformer l'utilisateur Firebase en User
    return await transformFirebaseUser(user);
  } catch (error: any) {
    console.error("Erreur lors de la connexion avec Google:", error);
    
    if (error.code === 'auth/user-not-found' || error.code === 'auth/account-exists-with-different-credential') {
      throw new Error("Aucun compte associé à cet email n'est autorisé dans l'application");
    }
    
    throw error;
  }
};

// Déconnexion avec logs détaillés
export const logout = async (): Promise<void> => {
  console.log("Firebase auth.ts: Starting signOut process");
  console.log("Firebase auth.ts: Current user:", auth.currentUser?.email);
  
  try {
    await signOut(auth);
    console.log("Firebase auth.ts: signOut completed successfully");
  } catch (error) {
    console.error("Firebase auth.ts: Error during signOut:", error);
    throw error;
  }
};

// Demande de réinitialisation de mot de passe
export const resetPasswordRequest = async (email: string): Promise<void> => {
  await sendPasswordResetEmail(auth, email);
};

// Confirmation de réinitialisation de mot de passe avec le code
export const confirmResetPassword = async (
  oobCode: string,
  newPassword: string
): Promise<void> => {
  await confirmPasswordReset(auth, oobCode, newPassword);
};

// Vérification du code OOB pour la réinitialisation
export const verifyPasswordResetCode = async (oobCode: string): Promise<string> => {
  return fbVerifyPasswordResetCode(auth, oobCode);
};

// Mise à jour du profil utilisateur
export const updateUserProfile = async (
  displayName: string,
  photoURL?: string
): Promise<void> => {
  if (!auth.currentUser) throw new Error('No user logged in');
  await updateProfile(auth.currentUser, { displayName, photoURL });
};

// Obtenir l'utilisateur courant
export const getCurrentUser = async (): Promise<User | null> => {
  const user = auth.currentUser;
  if (!user) return null;
  return transformFirebaseUser(user);
};

// Rafraîchir le token JWT
export const refreshToken = async (): Promise<string | null> => {
  if (!auth.currentUser) return null;
  return await auth.currentUser.getIdToken(true);
};
